"use client";

import type { JSX } from "react";
import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { XIcon } from "lucide-react";
import { Container } from "@/components/ui/Container";

/** Near end only — mid-read stays quiet (next article over subscribe). */
const SHOW_MIN_SCROLL_PCT = 80;
/** Hide as related / peak-end section enters view. */
const SHOW_MAX_SCROLL_PCT = 95;

interface ContinueReadingBarProps {
  slug: string;
  title: string;
  category?: string;
  imageUrl?: string;
  imageAlt?: string;
}

export function ContinueReadingBar({
  slug,
  title,
  category: _category,
  imageUrl,
  imageAlt,
}: ContinueReadingBarProps): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;

    const checkScroll = (): void => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = (window.scrollY / total) * 100;
      setVisible(pct >= SHOW_MIN_SCROLL_PCT && pct < SHOW_MAX_SCROLL_PCT);
    };

    checkScroll();
    window.addEventListener("scroll", checkScroll, { passive: true });
    return () => window.removeEventListener("scroll", checkScroll);
  }, [dismissed]);

  if (!visible || dismissed) return null;

  const href = `/blog/${slug}`;

  return (
    <aside
      aria-label="Continue reading suggestion"
      className="animate-slide-up fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur-sm safe-area-pb"
    >
      <Container
        size="md"
        padding="md"
        className="flex items-center gap-3 py-2.5 sm:gap-3"
      >
        <Link
          href={href}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
        >
          {imageUrl ? (
            <span className="relative hidden h-11 w-11 shrink-0 overflow-hidden rounded-md bg-gray-100 sm:block">
              <Image
                src={imageUrl}
                alt={imageAlt || title}
                fill
                className="object-cover"
                sizes="44px"
              />
            </span>
          ) : null}
          <span className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-500">
              Next article
            </p>
            <p className="mt-0.5 line-clamp-1 text-sm font-semibold text-gray-900">
              {title}
            </p>
          </span>
        </Link>
        <Link
          href={href}
          className="shrink-0 rounded-md bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
        >
          Read →
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
          aria-label="Dismiss continue reading suggestion"
        >
          <XIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </Container>
    </aside>
  );
}
